import React, { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from "~/hooks/redux";
import Preloader from "~/components/Shared/Preloader";
import { getSubscription } from "~/store/actions";
import { perms, useAccess } from './access';

const SubscriptionContext = React.createContext();

const SubscriptionProvider = props => {

  const dispatch = useAppDispatch();
  const { iAmNotGranted } = useAccess();

  const dealerStore = useAppSelector(state => state.Subscription.DealerStore.dealerStore);
  const isAuth = useAppSelector(state => state.Auth.Login.isAuth);

  // some users do not belong to a dealer store (ex. dealer upper management)
  // therefore 'dealerStore' may be NULL
  const paymentPlan = dealerStore?.paymentPlan;

  // returns TRUE if the dealer store is on a limited-time plan
  const isLimitedTimePlan = useCallback(() => !!paymentPlan && !!paymentPlan.maxDays, [paymentPlan]);

  // returns TRUE if the dealer store is on a limited-time plan and the grace period has expired
  const isPlanExpired = useCallback(() => isLimitedTimePlan() && paymentPlan.isExpired, [isLimitedTimePlan, paymentPlan]);

  // returns TRUE if the dealer store is NOT on a limited-time plan or the grace period has NOT expired yet
  const isPlanActive = useCallback(() => !isLimitedTimePlan() || !isPlanExpired(), [isLimitedTimePlan, isPlanExpired]);

  // returns the number of days left in a limited-time plan
  const planDaysLeft = useCallback(() => isLimitedTimePlan() ? paymentPlan.daysLeft : null, [isLimitedTimePlan, paymentPlan]);

  // returns TRUE if the user is required to pay for the services and the payment is not yet set up
  // or the user has a limited payment plan which has expired
  // some users (ex. dealer upper managers) are not required to pay
  const isPaymentSetupRequired = useCallback(() => iAmNotGranted(perms.free_of_charge_access) && (!dealerStore?.isPaymentSetupComplete || isPlanExpired()), [iAmNotGranted, dealerStore, isPlanExpired]);

  // returns TRUE if the dealer store should be forced to pay the subscription
  const isSubscriptionPaymentRequired = useCallback(() => dealerStore?.forceSubscriptionPayment, [dealerStore]);

  const isInstantPaymentRequired = useCallback(() => isSubscriptionPaymentRequired(), [isSubscriptionPaymentRequired]);

  const getPaymentStatementId = useCallback(() => dealerStore?.subscriptionStatement?.id, [dealerStore]);

  // returns TRUE if payment setup is completed
  const isPaymentSetupComplete = useCallback(() => dealerStore?.isPaymentSetupComplete, [dealerStore]);

  // returns TRUE if billing required fields are not completed
  const isStoreBillingInfoRequired = useCallback(() => !dealerStore.billingEmail || !dealerStore.billingName || !dealerStore.billingPhone, [dealerStore]);

  const refreshSubscription = useCallback(() => dispatch(getSubscription()), [dispatch]);

  // fetch subscription details from backend
  useEffect(() => {
    refreshSubscription();
  }, [refreshSubscription]);

  const value = useMemo(
    () => ({ dealerStore, paymentPlan, isPaymentSetupRequired, refreshSubscription, isLimitedTimePlan, isPlanExpired, isPlanActive, planDaysLeft, isPaymentSetupComplete, isStoreBillingInfoRequired, isInstantPaymentRequired, getPaymentStatementId }),
    [dealerStore, paymentPlan, isPaymentSetupRequired, refreshSubscription, isLimitedTimePlan, isPlanExpired, isPlanActive, planDaysLeft, isPaymentSetupComplete, isStoreBillingInfoRequired, isInstantPaymentRequired, getPaymentStatementId]
  );

  // wait until both auth (/me) and subscription (/me/subscription) have resolved
  // both requests fire in parallel because AuthContext no longer blocks its children with a preloader
  if (isAuth !== null && dealerStore !== undefined) {
    return <SubscriptionContext.Provider value={value} {...props} />
  }
  // until then show a preloader
  return <Preloader />
}

// helper hook that makes context data available
export const useSubscription = () => React.useContext(SubscriptionContext);

export default SubscriptionProvider;